"use client";

import React, { useEffect, useState } from "react";
import "../mpact2026.css";
import { CALENDAR_URL } from "../constants";

const NAV_LINKS = [
  { id: "overview", label: "Overview" },
  { id: "speakers", label: "Speakers" },
  { id: "panelists", label: "Panelists" },
  { id: "agenda", label: "Agenda" },
  { id: "venue", label: "Venue" },
];

const Nav = () => {
  const [activeId, setActiveId] = useState("overview");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const sections = NAV_LINKS.map((link) =>
      document.getElementById(link.id),
    ).filter((el): el is HTMLElement => Boolean(el));
    if (!sections.length || !("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-35% 0px -55% 0px", threshold: 0 },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string,
  ) => {
    const target = document.getElementById(id);
    setIsMenuOpen(false);
    if (!target) return;
    e.preventDefault();
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  return (
    <nav className="mpact-siteNav">
      <div className="mpact-wrap">
        <span className="mpact-navName">M-PACT 2026</span>
        <div className="mpact-navLinks">
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              className={activeId === link.id ? "mpact-active" : undefined}
              onClick={(e) => handleNavClick(e, link.id)}
            >
              {link.label}
            </a>
          ))}
        </div>
        <div className="mpact-navCta">
          <a href={CALENDAR_URL} target="_blank" rel="noopener noreferrer">
            Add to calendar
          </a>
        </div>

        <div className="mpact-navMobileCta">
          <a href={CALENDAR_URL} target="_blank" rel="noopener noreferrer">
            Add to calendar
          </a>
        </div>

        <button
          type="button"
          className="mpact-navMenuBtn"
          aria-label="Open menu"
          aria-expanded={isMenuOpen}
          aria-controls="mpact-mobileDrawer"
          onClick={() => setIsMenuOpen(true)}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 6h16M4 12h16M4 18h16"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div
        className={`mpact-navOverlay${isMenuOpen ? " mpact-navOverlay-open" : ""}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden="true"
      />

      <div
        id="mpact-mobileDrawer"
        className={`mpact-navDrawer${isMenuOpen ? " mpact-navDrawer-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
      >
        <div className="mpact-navDrawerHead">
          <span className="mpact-navName">M-PACT 2026</span>
          <button
            type="button"
            className="mpact-navCloseBtn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="mpact-navDrawerLinks">
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              className={activeId === link.id ? "mpact-active" : undefined}
              onClick={(e) => handleNavClick(e, link.id)}
            >
              {link.label}
            </a>
          ))}
        </div>
        <div className="mpact-navDrawerCta">
          <a href={CALENDAR_URL} target="_blank" rel="noopener noreferrer">
            Add to calendar
          </a>
        </div>
      </div>
    </nav>
  );
};

export default Nav;
